import { useEffect, useRef } from 'react';

/**
 * Custom cursor dot. Updates position via a ref + requestAnimationFrame so the
 * React tree is NEVER re-rendered on mousemove (the old setState-per-move
 * version re-rendered on every pixel, which made the cursor lag on heavy pages).
 */
export default function CustomCursor() {
  const ref = useRef(null);

  useEffect(() => {
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) return;
    const el = ref.current;
    if (!el) return;

    let x = -100;
    let y = -100;
    let raf = 0;
    let visible = false;

    const render = () => {
      raf = 0;
      el.style.transform = `translate3d(${x - 10}px, ${y - 10}px, 0)`;
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(render); };

    const move = (e) => {
      x = e.clientX;
      y = e.clientY;
      if (!visible) { visible = true; el.style.opacity = '1'; }
      schedule();
    };
    const leave = () => { visible = false; el.style.opacity = '0'; };
    const enter = () => { visible = true; el.style.opacity = '1'; };

    window.addEventListener('mousemove', move, { passive: true });
    document.addEventListener('mouseleave', leave);
    document.addEventListener('mouseenter', enter);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', move);
      document.removeEventListener('mouseleave', leave);
      document.removeEventListener('mouseenter', enter);
    };
  }, []);

  return (
    <div
      ref={ref}
      data-testid="custom-cursor"
      aria-hidden="true"
      className="fixed top-0 left-0 pointer-events-none z-[9999] rounded-full bg-white mix-blend-difference"
      style={{
        width: 20,
        height: 20,
        opacity: 0,
        willChange: 'transform',
        transform: 'translate3d(-100px, -100px, 0)',
        transition: 'opacity 0.2s ease',
      }}
    />
  );
}
